import type { ReactNode } from 'react';
import { Toaster, toast as sonnerToast } from 'sonner';

export interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Mount once at the app root. Toast styling is driven entirely by design tokens
 * so it follows light/dark automatically.
 */
export function ToastProvider({ children }: ToastProviderProps) {
  return (
    <>
      {children}
      <Toaster
        /* TOP right, because the BOTTOM right now belongs to the things that
           matter more: an incoming call and a new message. A transient "Saved"
           has no business sharing a corner with a ringing phone, and two fixed
           stacks in one corner overlap — there is no z-order that makes both
           readable. */
        position="top-right"
        /* Clear of the desktop window buttons; `env()` is 0 in a browser. */
        offset="calc(env(titlebar-area-height, 0px) + 20px)"
        gap={10}
        duration={4000}
        visibleToasts={4}
        toastOptions={{
          classNames: {
            toast:
              'group !rounded-xl !border !border-border !bg-card !text-card-foreground !shadow-dropdown !font-sans !text-md !gap-3 !p-4',
            title: '!font-semibold !text-foreground',
            description: '!text-sm !text-muted-foreground',
            actionButton:
              '!rounded-lg !bg-primary !px-3 !py-1.5 !text-base !font-semibold !text-primary-foreground',
            cancelButton:
              '!rounded-lg !bg-secondary !px-3 !py-1.5 !text-base !font-semibold !text-secondary-foreground',
            closeButton: '!rounded-md !border-border !bg-card !text-muted-foreground',
            success: '![--normal-border:hsl(var(--success))]',
            error: '![--normal-border:hsl(var(--destructive))]',
            warning: '![--normal-border:hsl(var(--warning))]',
            info: '![--normal-border:hsl(var(--info))]',
          },
        }}
      />
    </>
  );
}

/**
 * App-wide toast API. Keep messages short and human — never surface raw API errors.
 *   toast.success('Message sent.')
 *   toast.error('Could not join the meeting. Try again.')
 */
export const toast = sonnerToast;
